import { 
  collection, 
  doc, 
  getDoc, 
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query, 
  where,
  orderBy,
  onSnapshot,
  arrayRemove
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { createAuditLog } from './auditService';

const COLLECTION = 'barbershops';

/**
 * Criar nova barbearia
 */
export const createBarbershop = async (barbershopData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTION), {
      ...barbershopData,
      rating: 0,
      totalRatings: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating barbershop:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Buscar barbearia por ID
 */
export const getBarbershopById = async (barbershopId) => {
  try {
    const docRef = doc(db, COLLECTION, barbershopId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { success: true, data: { id: docSnap.id, ...docSnap.data() } };
    }
    return { success: false, error: 'Barbershop not found' };
  } catch (error) {
    console.error('Error getting barbershop:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Buscar todas as barbearias
 */
export const getAllBarbershops = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, COLLECTION));
    const barbershops = [];
    
    querySnapshot.forEach((doc) => {
      barbershops.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: barbershops };
  } catch (error) {
    console.error('Error getting barbershops:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Buscar barbearias patrocinadas
 */
export const getSponsoredBarbershops = async () => {
  try {
    const q = query(collection(db, COLLECTION), where('isSponsored', '==', true));
    const querySnapshot = await getDocs(q);
    const barbershops = [];
    
    querySnapshot.forEach((doc) => {
      barbershops.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: barbershops };
  } catch (error) {
    console.error('Error getting sponsored barbershops:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Atualizar barbearia
 */
export const updateBarbershop = async (barbershopId, updates) => {
  try {
    const docRef = doc(db, COLLECTION, barbershopId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: new Date().toISOString()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating barbershop:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Deletar barbearia (apenas o documento)
 */
export const deleteBarbershop = async (barbershopId) => {
  try {
    const docRef = doc(db, COLLECTION, barbershopId);
    await deleteDoc(docRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting barbershop:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Deletar barbearia e TODOS os dados relacionados
 * Remove: agendamentos, horários, avaliações, lista de espera,
 * convites, patrocínios, pagamentos e atualiza usuários vinculados.
 * ATENÇÃO: Esta ação é irreversível!
 */
export const deleteBarbershopAndAllData = async (barbershopId) => {
  try {
    // Buscar dados da barbearia antes de deletar (para log)
    const barbershopData = await getBarbershopById(barbershopId);
    const barbershopName = barbershopData.success ? barbershopData.data.name : 'Barbearia desconhecida';

    const deletedCounts = {};
    const errors = [];

    // Helper para deletar todos docs de uma collection por barbershopId
    const deleteCollectionDocs = async (collectionName, fieldName = 'barbershopId') => {
      try {
        const q = query(
          collection(db, collectionName),
          where(fieldName, '==', barbershopId)
        );
        const snapshot = await getDocs(q);
        const promises = snapshot.docs.map(d => deleteDoc(d.ref));
        await Promise.all(promises);
        deletedCounts[collectionName] = snapshot.size;
        return snapshot.size;
      } catch (err) {
        console.error(`Erro ao deletar ${collectionName}:`, err);
        errors.push(`${collectionName}: ${err.message}`);
        return 0;
      }
    };

    // 1. Deletar agendamentos
    await deleteCollectionDocs('appointments');

    // 2. Deletar horários disponíveis
    await deleteCollectionDocs('availableSlots');

    // 3. Deletar avaliações
    await deleteCollectionDocs('reviews');

    // 4. Deletar lista de espera
    await deleteCollectionDocs('waitlist');

    // 5. Deletar convites
    await deleteCollectionDocs('barbershopInvitations');

    // 6. Deletar patrocínios
    await deleteCollectionDocs('sponsors');

    // 7. Deletar pagamentos
    await deleteCollectionDocs('payments');

    // 8. Atualizar usuários que estão vinculados a esta barbearia
    try {
      // Formato novo: array barbershops
      const usersWithArrayQuery = query(
        collection(db, 'users'),
        where('barbershops', 'array-contains', barbershopId)
      );
      const usersWithArraySnap = await getDocs(usersWithArrayQuery);
      const updateUserPromises = usersWithArraySnap.docs.map(userDoc => 
        updateDoc(userDoc.ref, {
          barbershops: arrayRemove(barbershopId),
          updatedAt: new Date().toISOString()
        })
      );
      await Promise.all(updateUserPromises);
      deletedCounts.usersArrayUpdated = usersWithArraySnap.size;
    } catch (err) {
      console.error('Erro ao atualizar usuários (array):', err);
      errors.push(`users-array: ${err.message}`);
    }

    try {
      // Formato legado: string barbershopId
      const usersWithLegacyQuery = query(
        collection(db, 'users'),
        where('barbershopId', '==', barbershopId)
      );
      const usersWithLegacySnap = await getDocs(usersWithLegacyQuery);
      const updateLegacyPromises = usersWithLegacySnap.docs.map(userDoc =>
        updateDoc(userDoc.ref, {
          barbershopId: '',
          updatedAt: new Date().toISOString()
        })
      );
      await Promise.all(updateLegacyPromises);
      deletedCounts.usersLegacyUpdated = usersWithLegacySnap.size;
    } catch (err) {
      console.error('Erro ao atualizar usuários (legado):', err);
      errors.push(`users-legacy: ${err.message}`);
    }

    // 9. Deletar o documento da barbearia (SEMPRE executar, mesmo se passos anteriores falharem)
    const docRef = doc(db, COLLECTION, barbershopId);
    await deleteDoc(docRef);

    // 10. Registrar log de auditoria
    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        await createAuditLog({
          action: 'delete_barbershop',
          userId: currentUser.uid,
          userName: currentUser.displayName || currentUser.email,
          userRole: 'admin',
          targetType: 'barbershop',
          targetId: barbershopId,
          targetName: barbershopName,
          description: `Barbearia "${barbershopName}" deletada com todos os dados relacionados`,
          barbershopId: barbershopId,
          details: {
            deletedCounts,
            barbershopName,
            errors: errors.length > 0 ? errors : undefined
          }
        });
      }
    } catch (err) {
      console.error('Erro ao registrar log de auditoria:', err);
    }

    return { success: true, deletedCounts, warnings: errors.length > 0 ? errors : undefined };
  } catch (error) {
    console.error('Error deleting barbershop and all data:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Buscar barbearias por cidade
 */
export const getBarbershopsByCity = async (city) => {
  try {
    const q = query(collection(db, COLLECTION), where('city', '==', city));
    const querySnapshot = await getDocs(q);
    const barbershops = [];
    
    querySnapshot.forEach((doc) => {
      barbershops.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: barbershops };
  } catch (error) {
    console.error('Error getting barbershops by city:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Listener em tempo real para todas as barbearias
 */
export const subscribeToBarbershops = (callback) => {
  const q = query(collection(db, COLLECTION), orderBy('name'));
  
  return onSnapshot(q, (querySnapshot) => {
    const barbershops = [];
    querySnapshot.forEach((doc) => {
      barbershops.push({ id: doc.id, ...doc.data() });
    });
    callback(barbershops);
  }, (error) => {
    console.error('Error in barbershops listener:', error);
    callback([]);
  });
};

/**
 * Atualizar informações básicas da barbearia (nome, descrição, cidade, banner)
 */
export const updateBarbershopBasicInfo = async (barbershopId, { name, description, city, bannerUrl, isSponsored }) => {
  try {
    if (!name || !description || !city) {
      return { success: false, error: 'Nome, descrição e cidade são obrigatórios' };
    }

    // Buscar dados anteriores para comparação
    const previousData = await getBarbershopById(barbershopId);
    const oldData = previousData.success ? previousData.data : {};

    const updates = { name, description, city };
    if (bannerUrl) updates.bannerUrl = bannerUrl;
    if (typeof isSponsored === 'boolean') updates.isSponsored = isSponsored;

    const result = await updateBarbershop(barbershopId, updates);
    
    // Registrar log de auditoria
    if (result.success) {
      const currentUser = auth.currentUser;
      if (currentUser) {
        // Gerar descrição das mudanças
        const changes = [];
        const changeDetails = {};

        if (oldData.name !== name) {
          changes.push(`Nome alterado de "${oldData.name}" para "${name}"`);
          changeDetails.name = { before: oldData.name, after: name };
        }
        if (oldData.description !== description) {
          changes.push('Descrição atualizada');
          changeDetails.description = { before: oldData.description, after: description };
        }
        if (oldData.city !== city) {
          changes.push(`Cidade alterada de "${oldData.city}" para "${city}"`);
          changeDetails.city = { before: oldData.city, after: city };
        }
        if (bannerUrl && oldData.bannerUrl !== bannerUrl) {
          changes.push('Banner atualizado');
          changeDetails.bannerUrl = { before: oldData.bannerUrl, after: bannerUrl };
        }
        if (typeof isSponsored === 'boolean' && oldData.isSponsored !== isSponsored) {
          changes.push(isSponsored ? 'Marcada como patrocinada' : 'Removida de patrocinada');
          changeDetails.isSponsored = { before: oldData.isSponsored || false, after: isSponsored };
        }

        const changeDescription = changes.length > 0 
          ? changes.join('; ') 
          : 'Informações atualizadas';

        await createAuditLog({
          action: 'update_barbershop',
          userId: currentUser.uid,
          userName: currentUser.displayName || currentUser.email,
          userRole: 'admin',
          targetType: 'barbershop',
          targetId: barbershopId,
          targetName: name,
          description: changeDescription,
          changes: changeDetails,
          barbershopId: barbershopId,
          details: {
            updatedFields: Object.keys(updates),
            barbershopName: name
          }
        });
      }
    }
    
    return result;
  } catch (error) {
    console.error('Error updating basic info:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Atualizar informações de contato (endereço e telefone)
 */
export const updateBarbershopContactInfo = async (barbershopId, { address, phone }) => {
  try {
    const updates = {};
    if (address !== undefined) updates.address = address;
    if (phone !== undefined) updates.phone = phone;

    return await updateBarbershop(barbershopId, updates);
  } catch (error) {
    console.error('Error updating contact info:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Atualizar lista de serviços da barbearia
 */
export const updateBarbershopServices = async (barbershopId, services) => {
  try {
    if (!Array.isArray(services)) {
      return { success: false, error: 'Serviços devem ser um array' };
    }

    // Validar estrutura dos serviços
    for (const service of services) {
      if (!service.name || service.price === undefined || !service.duration) {
        return { success: false, error: 'Cada serviço deve ter nome, preço e duração' };
      }
    }

    // Buscar dados anteriores
    const previousData = await getBarbershopById(barbershopId);
    const barbershopName = previousData.success ? previousData.data.name : 'Barbearia';

    const result = await updateBarbershop(barbershopId, { services });
    
    // Registrar log de auditoria
    if (result.success) {
      const currentUser = auth.currentUser;
      if (currentUser) {
        const serviceNames = services.map(s => s.name).join(', ');
        const description = `Serviços atualizados: ${serviceNames} (${services.length} serviço${services.length > 1 ? 's' : ''})`;

        await createAuditLog({
          action: 'update_services',
          userId: currentUser.uid,
          userName: currentUser.displayName || currentUser.email,
          userRole: 'owner',
          targetType: 'barbershop',
          targetId: barbershopId,
          targetName: barbershopName,
          description: description,
          barbershopId: barbershopId,
          details: {
            servicesCount: services.length,
            serviceNames: services.map(s => s.name)
          }
        });
      }
    }
    
    return result;
  } catch (error) {
    console.error('Error updating services:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Atualizar lista de barbeiros da barbearia
 */
export const updateBarbershopBarbers = async (barbershopId, barbers) => {
  try {
    if (!Array.isArray(barbers)) {
      return { success: false, error: 'Barbeiros devem ser um array' };
    }

    // Validar estrutura dos barbeiros
    for (const barber of barbers) {
      if (!barber.name || !barber.specialty || !barber.experience) {
        return { success: false, error: 'Cada barbeiro deve ter nome, especialidade e experiência' };
      }
    }

    return await updateBarbershop(barbershopId, { barbers });
  } catch (error) {
    console.error('Error updating barbers:', error);
    return { success: false, error: error.message };
  }
};
